"use client";

import Image from "next/image";
import { motion, Variants } from "framer-motion";
import { ASSET_PREFIX } from "@/config";
import { SUBBANNER_CONFIG, SubBannerKey } from "../../config/subbanner.config";

type Props = {
  pageKey?: SubBannerKey;         // pass key from page OR use default
  titleOverride?: string;         // optional direct override
};

export default function BlogSubBanner({ pageKey = "default", titleOverride }: Props) {
  const cfg = SUBBANNER_CONFIG[pageKey] ?? SUBBANNER_CONFIG.default;

const shuttleVariants: Variants = {
  initial: {
    // Start completely off-screen to the right
    x: "100vw", 
    y: -20,
    scale: 0.5,
    opacity: 1,
  },
  enter: {
    // Moves to its container's relative "0" 
    // (Adjust the 'right' class in the JSX to control this landing spot)
    x: [0, 305, -5, 0],
    y: 0,
    scale: 1, 
    opacity: 1,
    transition: {
      duration: 3.5,
      ease: [0.22, 1, 0.36, 1], // Custom cubic-bezier for a "smooth glide" feel
    },
  },
  float: {
    // Gentle hovering movement once it arrives
    x: [0, 5, -5, 0],
    y: [0, -10, 10, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};


  const title = titleOverride ?? cfg.title;

  return (
    <section className="h-[350px] sm:h-[450px] xl:h-[450px] max-h-[450px] overflow-hidden relative">
      {/* Background Layer */}
      <div
        className={[
          "w-full h-full relative top-[82px] md:top-[92px] bg-cover",
          "after:absolute after:w-[100%] after:h-[100%] after:top-[0px] after:right-[0px]",
          cfg.overlay ??
            "after:bg-[linear-gradient(90deg,_#000000d4_24%,_#0000_75%)]",
        ].join(" ")}
      >
        <Image
          src={`${ASSET_PREFIX}${cfg.bg}`}
          alt={`${pageKey} subbanner`}
          width={1920}
          height={700}
          className="w-full h-full object-cover z-0 object-top"
          priority
        />
      </div>

      {/* Text Layer */}
      <div className="absolute bottom-0 left-[4.5%] z-10 text-white pl-[3%] pb-[50px] sm:pb-[80px] w-[90.5%] flex md:flex-row flex-col justify-between items-start md:items-end gap-0 md:gap-[50px]">
        <p className="text-[38px] sm:text-[40px] lg:text-[46px] xl:text-[54px] 2xl:text-[60px] leading-[48px] sm:leading-[54px] lg:leading-[62px] 2xl:leading-[68px] font-light tracking-[-0.5px] mb-0 whitespace-pre-line">
          {title}
        </p>
      </div>
 
      {/* Space Shuttle Layer */}
{cfg.showShuttle && cfg.shuttle && (
  <motion.div
    variants={shuttleVariants}
    initial="initial"
    animate={["enter", "float"]}
    className={[
      "absolute z-20 pointer-events-none",
      // Landing position: Adjusted to sit over the pergola area
      cfg.shuttlePosClass ?? "top-[180px] sm:top-[120px] left-auto right-[10%] sm:right-[40%]",
    ].join(" ")}
  >
    <Image
      src={`${ASSET_PREFIX}${cfg.shuttle}`}
      alt="Space Shuttle"
      width={300}
      height={300}
      className="max-[480px]:w-[120px] w-[180px] sm:w-[280px] xl:w-[350px] h-auto object-contain"
      priority
    />
  </motion.div>
)}
    </section>
  );
}
